import {
  LiveblogApi,
  LiveblogWidgetQueryProps,
  LiveblogWidgetQueryPropsParser,
  LiveblogWidgetTagProps,
  liveblogWidgetTagPropsParser,
} from "./liveblog/LiveblogWidgetProps";
import {LiveblogThemeConfiguration, SharedThemeConfiguration,} from "@webng-types/embedjs";
import {notInitialized} from "../utils/notInitialized";
import {AdManager} from "../ads/AdManager";
import {NavigateToEventIdOptions} from "./liveblog-core/LiveblogCoreWidgetProps";
import React from "react";
import {AbstractLiveblogElement} from "./AbstractLiveblogElement";
import {PropsParser} from "@webng/validations";
import {LiveblogWidget} from "./liveblog/LiveblogWidget";
import {LiveblogData} from "./liveblog-core/liveblogDataReducer";
import {LiveblogRenderPortal, LiveblogRenderPortalTypes} from "@webng/liveblog";
import {
  parsePartialLiveblogThemeConfiguration,
  rawLiveblogThemeConfigurationSchema
} from "@webng-types/embedjs/liveblog";
import {rawSharedThemeConfigurationSchema} from "@webng-types/embedjs/shared";

const propsParser: PropsParser<LiveblogWidgetTagProps> = {
  ...liveblogWidgetTagPropsParser,
};

export class TickarooLiveblogElement
  extends AbstractLiveblogElement<LiveblogThemeConfiguration, LiveblogWidgetTagProps, LiveblogWidgetQueryProps, LiveblogApi>
  implements LiveblogApi
{
  // consent set via the api, kept so calls made before the widget has
  // rendered apply on mount and survive full widget re-renders
  private readonly apiConsent: Map<string, boolean> = new Map();

  // only replay the map when a render/hydrate cycle produced a fresh widget;
  // the ref callback also refires on imperative-handle recreation during data
  // loads, where replaying would override consent the user changed interactively
  private consentFlushPending = true;

  static get observedAttributes() {
    return [
      ...Object.keys(propsParser).map((x) => x.toLowerCase()),
      ...Object.keys(rawLiveblogThemeConfigurationSchema.shape).map((x) =>
        x.toLowerCase()
      ),
      ...Object.keys(rawSharedThemeConfigurationSchema.shape).map((x) =>
        x.toLowerCase()
      ),
    ];
  }


  constructor() {
    super(liveblogWidgetTagPropsParser, LiveblogWidgetQueryPropsParser);
  }

  hasEventIdLoaded(eventId: string): boolean {
    if (this.liveblogComponent) {
      return this.liveblogComponent.hasEventIdLoaded(eventId);
    } else {
      return notInitialized();
    }
  }

  loadMoreBottom(): PromiseLike<void> {
    if (this.liveblogComponent) {
      return this.liveblogComponent.loadMoreBottom();
    } else {
      return notInitialized();
    }
  }

  loadMoreTop(): PromiseLike<void> {
    if (this.liveblogComponent) {
      return this.liveblogComponent.loadMoreTop();
    } else {
      return notInitialized();
    }
  }

  setConsent(provider: string, consent: boolean): void {
    this.apiConsent.set(provider, consent);
    if (this.liveblogComponent) {
      this.liveblogComponent.setConsent(provider, consent);
    }
  }

  renderWidget(el: Element, props: {
    tagProps: LiveblogWidgetTagProps
    queryProps: LiveblogWidgetQueryProps
    overrideSharedConfiguration: Partial<SharedThemeConfiguration>
    overrideConfiguration: Partial<LiveblogThemeConfiguration>
  }, refCallback: (ref: LiveblogApi) => void) {
    this.consentFlushPending = true;
    super.renderWidget(el, props, refCallback);
  }

  hydrateWidget(el: Element, props: {
    tagProps: LiveblogWidgetTagProps
    queryProps: LiveblogWidgetQueryProps
    overrideSharedConfiguration: Partial<SharedThemeConfiguration>
    overrideConfiguration: Partial<LiveblogThemeConfiguration>
  }, refCallback: (ref: LiveblogApi) => void) {
    this.consentFlushPending = true;
    return super.hydrateWidget(el, props, refCallback);
  }

  protected attachComponent(ref: LiveblogApi) {
    super.attachComponent(ref);
    if (ref && this.consentFlushPending) {
      this.consentFlushPending = false;
      this.apiConsent.forEach((consent, provider) => ref.setConsent(provider, consent));
    }
  }

  setAdManager(newAdManager: AdManager): void {
    if (this.liveblogComponent) {
      return this.liveblogComponent.setAdManager(newAdManager)
    } else {
      return notInitialized()
    }
  }

  getCurrentLiveblogData(): LiveblogData {
    if(this.liveblogComponent) {
      return this.liveblogComponent.getCurrentLiveblogData();
    } else {
      return notInitialized()
    }
  }

  navigateToEventId(eventId: string, options?: NavigateToEventIdOptions): PromiseLike<boolean> {
    if (this.liveblogComponent) {
      return this.liveblogComponent.navigateToEventId(eventId, options);
    } else {
      return notInitialized();
    }
  }

  registerPortalForType(type: LiveblogRenderPortalTypes, portal: LiveblogRenderPortal): void {
    if (this.liveblogComponent) {
      return this.liveblogComponent.registerPortalForType(type, portal);
    } else {
      return notInitialized();
    }
  }

  unregisterPortalForType(type: LiveblogRenderPortalTypes, portal: HTMLElement): void {
    if (this.liveblogComponent) {
      return this.liveblogComponent.unregisterPortalForType(type, portal);
    } else {
      return notInitialized();
    }
  }

  parseConfigurationProps(metaValues: Record<string, string>, elementAttributes: Record<string, string>): Partial<LiveblogThemeConfiguration> {
    const metaValuesConfiguration = parsePartialLiveblogThemeConfiguration(metaValues);
    const elementAttributesConfiguration = parsePartialLiveblogThemeConfiguration(elementAttributes);
    return Object.assign({}, metaValuesConfiguration, elementAttributesConfiguration);
  }

  useHydrate(props: {
    tagProps: LiveblogWidgetTagProps;
    queryProps: LiveblogWidgetQueryProps;
    overrideSharedConfiguration: Partial<SharedThemeConfiguration>;
    overrideConfiguration: Partial<LiveblogThemeConfiguration>
  }): boolean {
    return !!props.tagProps.initialData.configuration;
  }

  renderImp(ref: (r: LiveblogApi) => void, props: {
    tagProps: LiveblogWidgetTagProps
    queryProps: LiveblogWidgetQueryProps
    overrideSharedConfiguration: Partial<SharedThemeConfiguration>
    overrideConfiguration: Partial<LiveblogThemeConfiguration>
  }): React.ReactNode {
    const finalProps = Object.assign({}, props.tagProps, props.queryProps);
    // The initialData attribute is not cleared when a host swaps liveblogId, so it can belong to another liveblog.
    const initialGame = finalProps.initialData.gameShowResponse?.game
    if (initialGame && initialGame.ticker_id !== finalProps.liveblogId && initialGame.local_id !== finalProps.liveblogId) {
      finalProps.initialData = {}
    }
    return <LiveblogWidget key={finalProps.liveblogId} ref={ref} {...finalProps} overrideSharedThemeConfiguration={props.overrideSharedConfiguration} overrideLiveblogThemeConfiguration={props.overrideConfiguration} />
  }
}
